import { EApplicantType, EDocumentStatus, EDocumentType, EExternalStep } from '@if/core/types'
import { CheckCircle, ErrorOutline as ErrorIcon } from '@mui/icons-material'
import { Grid, Stack, Typography } from '@mui/material'
import { green } from '@mui/material/colors'
import { useTranslation } from 'react-i18next'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useIsConfirmJobIncomeCompleted } from '@src/data/selectors/credit-selectors'
import { CreditApplication, RequiredExternalStep, User } from '../../../data/types'
import FlinksActionsSection from './FlinksActionsSection'

interface BankInformationSectionProps {
  applicantType: EApplicantType
  creditApplication: CreditApplication
  handleSaveBankAccountRequest: (applicantType: EApplicantType) => void
  handleResetBankAccountRequest: (applicantType: EApplicantType) => void
  findRequiredStepsByApplicantTypeAndExternalStepId: (
    applicantType: EApplicantType,
    externalStepId: EExternalStep,
  ) => RequiredExternalStep | undefined
  user: User | null
  editDisabled: boolean
  worksheetIsApproved: boolean
}

const BankInformationSection = ({
  applicantType,
  creditApplication,
  handleSaveBankAccountRequest,
  handleResetBankAccountRequest,
  findRequiredStepsByApplicantTypeAndExternalStepId,
  user,
  editDisabled,
  worksheetIsApproved,
}: BankInformationSectionProps) => {
  const { t } = useTranslation()
  const { documents: docs } = useCreditApplicationData()
  const confirmJobIncomeTaskCompleted = useIsConfirmJobIncomeCompleted(applicantType)

  const voidChequeReceived = docs.some(
    (doc) =>
      doc.typeId === EDocumentType.VoidCheque &&
      doc.applicantType === applicantType &&
      ![
        EDocumentStatus.Refused,
        EDocumentStatus.Deleted,
        EDocumentStatus.AwaitingDocument,
        EDocumentStatus.Incomplete,
      ].includes(doc.status ?? EDocumentStatus.AwaitingDocument),
  )

  return (
    <>
      <Grid container direction="row" rowSpacing={2} spacing={2} marginBottom={1}>
        <Grid>
          <Typography variant="h5" component="h5" color="primary">
            {t('bankAccount.title')} ({t(`common.${applicantType}`)}):
          </Typography>
        </Grid>
        <Grid>
          <Stack direction="row">
            {voidChequeReceived ? (
              <>
                <CheckCircle color={'success'} fontSize="large" style={{ paddingRight: '1%' }} />
                <Typography marginTop={0.3} variant="h5" component="h5" style={{ color: green[500] }}>
                  {t('common.voidChequeReceived')}
                </Typography>
              </>
            ) : (
              <>
                <ErrorIcon color="warning" fontSize="large" style={{ paddingRight: '1%' }} />
                <Typography marginTop={0.3} variant="h5" component="h5">
                  {t('common.voidChequePending')}
                </Typography>
              </>
            )}
          </Stack>
        </Grid>
      </Grid>

      <Grid container direction="row" spacing={2}>
        <Grid width="100%">
          <FlinksActionsSection
            applicantType={applicantType}
            handleSaveBankAccountRequest={handleSaveBankAccountRequest}
            handleResetBankAccountRequest={handleResetBankAccountRequest}
            requiredExternalStep={findRequiredStepsByApplicantTypeAndExternalStepId(
              applicantType,
              EExternalStep.BankAccount,
            )}
            user={user}
            creditApplication={creditApplication}
            editDisabled={editDisabled || voidChequeReceived}
            worksheetIsApproved={worksheetIsApproved}
            creditApplicationId={creditApplication.id}
            confirmJobIncomeTaskCompleted={confirmJobIncomeTaskCompleted}
            showHistoryDialog={false}
          />
        </Grid>
      </Grid>
    </>
  )
}

export default BankInformationSection
